import React, { useMemo, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import type { OrgChartRow } from '../../../support/orgScope';
import {
  STAGE_LABELS,
  journeyMapTotals,
  overallStage,
  participationFor,
  pctOf,
  summarizeParticipation,
  type JourneyMapRollupResponse,
  type ParticipationRollupResponse,
} from '../../../support/campaignParticipation';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignChartCard, { ChartGrid, DASH, KpiCards } from './CampaignChartCard';

interface ReportState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
}

interface Props {
  orgRows: OrgChartRow[];
  /** The selected leader; "people in scope" is everyone in their org. */
  manager: string;
  participation: ReportState<ParticipationRollupResponse>;
  maps: ReportState<JourneyMapRollupResponse>;
  /** "All campaigns" / campaign name, plus the period when one is selected. */
  scopeLabel: string;
  /** The campaign name or "All campaigns", without the period (journey maps are not period-filtered). */
  campaignScope: string;
  /** True when a time period narrows the deliveries (enrollment and enablement are never period-filtered). */
  periodActive: boolean;
  keyPrefix: string;
}

const unavailable = (what: string, error: Error | null) => (
  <div className="hbr-css__text-secondary-sm" style={{ padding: 4 }}>
    {`${what} isn't available right now${error?.message ? ` (${error.message})` : ''}. If the server was updated recently it may not have the new reports yet.`}
  </div>
);

const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px' };
const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '6px 8px', borderTop: `1px solid ${DASH.border}` };
const tdRight: React.CSSProperties = { ...td, textAlign: 'right' };

/** A small card-styled table, matching the pipeline status table. */
function CardTable({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: DASH.surface,
        border: `1px solid ${DASH.border}`,
        borderRadius: DASH.radius,
        boxShadow: DASH.shadowCard,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${DASH.border}` }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: DASH.text }}>{title}</div>
        {subtitle ? <div style={{ fontSize: '0.75rem', color: DASH.textDim, marginTop: 2 }}>{subtitle}</div> : null}
      </div>
      <div style={{ padding: '12px 14px', overflowX: 'auto' }}>{children}</div>
    </div>
  );
}

/**
 * The Campaigns tab's participation reports for a leader's org: the participation funnel (enrolled →
 * enablement → deliveries) with where everyone is now and how far through enablement people are, and the
 * journey maps broken out by map. Everyone in the leader's org is "in scope"; no user list is involved.
 */
export default function CampaignsParticipationSections({
  orgRows,
  manager,
  participation,
  maps,
  scopeLabel,
  campaignScope,
  periodActive,
  keyPrefix,
}: Props) {
  const [funnelOpen, setFunnelOpen] = useState(true);
  const [mapsOpen, setMapsOpen] = useState(true);

  const summary = useMemo(
    () => summarizeParticipation(orgRows, manager, participation.data),
    [orgRows, manager, participation.data],
  );
  const mapRows = useMemo(() => maps.data?.maps ?? [], [maps.data]);
  const tableRows = useMemo(
    () => mapRows.flatMap((m) => [{ row: m, child: false }, ...(m.children ?? []).map((c) => ({ row: c, child: true }))]),
    [mapRows],
  );
  const totals = useMemo(() => journeyMapTotals(mapRows), [mapRows]);

  return (
    <>
      <StbAccordion
        variant="minimal"
        label={`Participation funnel — ${scopeLabel}`}
        isOpen={funnelOpen}
        toggleIsOpen={setFunnelOpen}
      >
        <div style={{ padding: 12 }}>
          {participation.loading ? (
            <HbrSpinner>Loading participation…</HbrSpinner>
          ) : participation.error ? (
            unavailable('The participation funnel', participation.error)
          ) : (
            <>
              <KpiCards
                kpis={[
                  { label: 'People in scope', value: summary.peopleInScope },
                  ...summary.funnel.slice(0, 4).map((r) => ({ label: r.label, value: r.people })),
                ]}
              />
              <ChartGrid>
                <CardTable title="Participation funnel" subtitle={`${summary.peopleInScope} people in this org`}>
                  <table className="w-full text-sm" style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                    <thead>
                      <tr className="hbr-css__text-secondary-xs">
                        <th style={th}>Milestone</th>
                        <th style={thRight}>People</th>
                        <th style={thRight}>% of org</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.funnel.map((r) => (
                        <tr key={r.key}>
                          <td style={td}>{r.label}</td>
                          <td style={tdRight}>{r.people}</td>
                          <td style={tdRight}>{pctOf(r.people, summary.peopleInScope)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardTable>
                <CampaignChartCard
                  title="Funnel"
                  subtitle="People reaching each milestone"
                  chartType="horizontal_bar_chart"
                  data={summary.funnel.map((r) => ({ label: r.label, value: r.people }))}
                  tileKey={`${keyPrefix}-funnel`}
                  tooltipKeys={{ label: 'Milestone', value: 'People' }}
                  settings={{ valueFormat: 'number', axisLeftLegendOffset: -200, marginLeft: 220, maxLeftMarginRatio: 0.5 }}
                  empty="No participation yet."
                />
                <CampaignChartCard
                  title="Where everyone is now"
                  subtitle="Furthest stage reached"
                  chartType="donut_chart"
                  data={summary.stages.map((r) => ({ label: r.label, value: r.people }))}
                  tileKey={`${keyPrefix}-stages`}
                  tooltipKeys={{ label: 'Stage', value: 'People' }}
                  empty="No one in this org yet."
                />
                <CampaignChartCard
                  title="Enablement progress"
                  subtitle="People with assigned maps, by share of items done"
                  chartType="horizontal_bar_chart"
                  data={summary.progressBuckets.map((r) => ({ label: r.label, value: r.people }))}
                  tileKey={`${keyPrefix}-buckets`}
                  tooltipKeys={{ label: 'Items done', value: 'People' }}
                  settings={{ valueFormat: 'number', axisLeftLegendOffset: -120, marginLeft: 140, maxLeftMarginRatio: 0.5 }}
                  empty="No one has been assigned enablement."
                />
              </ChartGrid>
              <div className="hbr-css__text-secondary-sm" style={{ marginTop: 8 }}>
                {periodActive
                  ? 'Deliveries follow the selected period. '
                  : ''}
                Enrollment, deliveries and enablement follow the selected campaign (enablement counts the journey
                maps configured on it); enablement is never filtered by period. Everyone in this org counts as
                “in scope”.
              </div>
            </>
          )}
        </div>
      </StbAccordion>

      <StbAccordion variant="minimal" label={`Journey maps by map — ${campaignScope}`} isOpen={mapsOpen} toggleIsOpen={setMapsOpen}>
        <div style={{ padding: 12 }}>
          {maps.loading ? (
            <HbrSpinner>Loading journey maps…</HbrSpinner>
          ) : maps.error ? (
            unavailable('The journey-map report', maps.error)
          ) : mapRows.length === 0 ? (
            <div className="hbr-css__text-secondary-sm">
              {maps.data?.campaign != null
                ? 'No journey maps are configured on this campaign’s enablement steps.'
                : 'No journey maps are configured on any campaign’s enablement steps.'}
            </div>
          ) : (
            <>
              <ChartGrid>
                <CardTable title="Journey map status by map" subtitle="Effective status of each active assignment">
                  <table className="w-full text-sm" style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                    <thead>
                      <tr className="hbr-css__text-secondary-xs">
                        <th style={th}>Journey map</th>
                        <th style={thRight}>Items</th>
                        <th style={thRight}>Assigned</th>
                        <th style={thRight}>Not started</th>
                        <th style={thRight}>In progress</th>
                        <th style={thRight}>Completed</th>
                        <th style={thRight}>% completed</th>
                        <th style={thRight}>Avg progress</th>
                        <th style={thRight}>Avg days to complete</th>
                        <th style={thRight}>Out of sync</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map(({ row: m, child }) => (
                        <tr key={`${child ? 'c' : 'm'}-${m.journey_map_id}`} style={child ? { color: DASH.textDim } : undefined}>
                          <td style={child ? { ...td, paddingLeft: 24 } : td}>
                            {child ? `↳ ${m.name}` : m.name}
                            {!child && m.kind === 'composite' ? (
                              <span style={{ marginLeft: 6, fontSize: '0.75rem', color: DASH.textDim }}>path</span>
                            ) : null}
                          </td>
                          <td style={tdRight}>{m.items}</td>
                          <td style={tdRight}>{m.assigned}</td>
                          <td style={tdRight}>{m.not_started}</td>
                          <td style={tdRight}>{m.in_progress}</td>
                          <td style={tdRight}>{m.completed}</td>
                          <td style={tdRight}>{pctOf(m.completed, m.assigned)}%</td>
                          <td style={tdRight}>{Math.round(m.avg_progress)}%</td>
                          <td style={tdRight}>
                            {m.avg_days_to_complete == null ? '—' : m.avg_days_to_complete.toFixed(1)}
                          </td>
                          <td style={tdRight}>{m.out_of_sync}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700 }}>
                        <td style={td}>{maps.data?.campaign != null ? 'All maps in this campaign' : 'All campaign journey maps'}</td>
                        <td style={tdRight}>—</td>
                        <td style={tdRight}>{totals.assigned}</td>
                        <td style={tdRight}>{totals.not_started}</td>
                        <td style={tdRight}>{totals.in_progress}</td>
                        <td style={tdRight}>{totals.completed}</td>
                        <td style={tdRight}>{pctOf(totals.completed, totals.assigned)}%</td>
                        <td style={tdRight}>—</td>
                        <td style={tdRight}>—</td>
                        <td style={tdRight}>{totals.out_of_sync}</td>
                      </tr>
                    </tbody>
                  </table>
                </CardTable>
                <CampaignChartCard
                  title="% completed by journey map"
                  subtitle="Completed of assigned"
                  chartType="horizontal_bar_chart"
                  data={mapRows.map((m) => ({ label: m.name, value: pctOf(m.completed, m.assigned) }))}
                  tileKey={`${keyPrefix}-maps-completed`}
                  tooltipKeys={{ label: 'Journey map', value: '% completed' }}
                  settings={{ valueFormat: 'number', axisLeftLegendOffset: -200, marginLeft: 220, maxLeftMarginRatio: 0.5 }}
                  empty="Nothing completed yet."
                />
              </ChartGrid>
              <div className="hbr-css__text-secondary-sm" style={{ marginTop: 8 }}>
                A map counts as completed when the assignment is completed or every item is done, and as in
                progress when the assignment is in progress or any item is done, so a recorded status that lags
                item progress is still counted. “Out of sync” is a recorded status that disagrees with item
                progress. Only the maps configured on the selected campaign’s enablement steps are listed (all
                campaigns: every map any campaign uses); a path shows its own status with its maps beneath it.
                Enablement is not filtered by period.
              </div>
            </>
          )}
        </div>
      </StbAccordion>
    </>
  );
}

interface IndividualProps {
  email: string;
  orgRows: OrgChartRow[];
  participation: ReportState<ParticipationRollupResponse>;
  /** "All campaigns" / campaign name, plus the period when one is selected. */
  scopeLabel: string;
  /** True when a time period narrows the deliveries (enrollment and enablement are never period-filtered). */
  periodActive: boolean;
}

/**
 * The participation funnel for one person: the same milestones as the team funnel, shown as reached or not
 * (a funnel of one has no people counts or percentages), with where they are now and how far through their
 * enablement items they are.
 */
export function CampaignsParticipationIndividual({
  email,
  orgRows,
  participation,
  scopeLabel,
  periodActive,
}: IndividualProps) {
  const [open, setOpen] = useState(true);

  const person = useMemo(() => participationFor(participation.data, email), [participation.data, email]);
  const summary = useMemo(
    () => summarizeParticipation(orgRows, email, participation.data),
    [orgRows, email, participation.data],
  );
  const itemsPct = pctOf(person.items_done, person.items_total);

  return (
    <StbAccordion variant="minimal" label={`Participation funnel — ${scopeLabel}`} isOpen={open} toggleIsOpen={setOpen}>
      <div style={{ padding: 12 }}>
        {participation.loading ? (
          <HbrSpinner>Loading participation…</HbrSpinner>
        ) : participation.error ? (
          unavailable('The participation funnel', participation.error)
        ) : (
          <>
            <KpiCards
              kpis={[
                { label: 'Furthest stage', value: STAGE_LABELS[overallStage(person)] },
                { label: 'Maps completed', value: `${person.maps_completed} of ${person.maps_assigned}` },
                {
                  label: 'Enablement items done',
                  value: person.items_total > 0 ? `${person.items_done} of ${person.items_total} (${itemsPct}%)` : '—',
                },
              ]}
            />
            <ChartGrid>
              <CardTable title="Participation funnel" subtitle="Milestones this person has reached">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr className="hbr-css__text-secondary-xs">
                      <th style={th}>Milestone</th>
                      <th style={thRight}>Reached</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.funnel.map((r) => (
                      <tr key={r.key}>
                        <td style={td}>{r.label}</td>
                        <td style={tdRight}>{r.people > 0 ? 'Yes' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardTable>
            </ChartGrid>
            <div className="hbr-css__text-secondary-sm" style={{ marginTop: 8 }}>
              {periodActive ? 'Deliveries follow the selected period. ' : ''}
              Enrollment, deliveries and enablement follow the selected campaign (enablement counts the journey maps
              configured on it); enablement is never filtered by period.
            </div>
          </>
        )}
      </div>
    </StbAccordion>
  );
}
